import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Body2,
  Button,
  Caption2,
  Card,
  CardHeader,
  Link,
  mergeClasses,
  Persona,
  Spinner,
  Tag,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import {
  Apps24Regular,
  Clipboard16Filled,
  Clipboard16Regular,
  Document16Regular,
  Edit16Filled,
  Edit16Regular,
  Pin16Regular,
  Settings16Regular,
} from "@fluentui/react-icons";
import { ConstantValues, QueryCacheConfig } from "common/constants";
import { Charts } from "components/charts/Charts";
import { useResizer } from "hooks/useResizer";
import { ICitation } from "models/ICitation";
import { IConversation } from "models/IConversation";
import { IFile } from "models/IFile";
import { IMessage } from "models/IMessage";
import { IMessageComment } from "models/IMessageComment";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "store";
import { downloadFile } from "utils/blobUtils";
import { renderTemplateIcon } from "utils/templateIcons";
import {
  buildCitationKeyMap,
  getCitationKey,
  getTimeAndDate,
  renderContentWithCitations,
} from "utils/generalUtils";
import { useQuery } from "react-query";
import { ITemplate } from "models/ITemplate";
import {
  addCommentToMessage,
  getTemplates,
  getMessagesOfConversation,
} from "../../../api";
import { resolveAsset } from "../../../common/fileIcons";
import { setCurrentMessages } from "../../../redux/messages";
import { AIResponse } from "../AIResponse";
import { AddCommentDialog } from "../comments/addCommentDialog/AddCommentDialog";
import { MessageComment } from "../comments/messageComment/MessageComment";
import { SkeletonChat } from "../skeletonChat/SkeletonChat";
import { ThumbsFeedback } from "../userFeedback/thumbsFeedback/ThumbsFeedback";
import { useStyles } from "./ConversationMessages.styles";
import { IConversationMessagesProps } from "./ConversationMessages.types";

export const ConversationMessages = ({
  isMessageLoading,
  autoScrollEnabled,
  setAutoScrollEnabled,
  isEditMessageActive,
  setIsEditMessageActive,
  setPromptToEdit,
  onOpenGallery,
  onUseTemplate,
}: IConversationMessagesProps) => {
  const currentSelectedConversation: IConversation = useSelector(
    (state: RootState) => state.currentConversationSetter.value,
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const currentMessages: IMessage[] = useSelector(
    (state: RootState) => state.currentMessagesSetter.value,
  );
  const activeWorkspace = useSelector(
    (state: RootState) => state.activeWorkspaceSlice.value,
  );
  const isMobile: boolean = useResizer();
  const navigate = useNavigate();

  const { data: chatTemplatesResponse, isLoading: isLoadingTemplates } =
    useQuery("chat-screen-templates", getTemplates, {
      refetchOnWindowFocus: false,
      staleTime: QueryCacheConfig.STALE_TIME_DEFAULT,
    });
  const chatTemplates: ITemplate[] = (
    chatTemplatesResponse?.templates || []
  ).filter(
    (template: ITemplate) => template.isActive && template.showOnChatScreen,
  );

  const aiGeneratedMessage: string = useSelector(
    (state: RootState) => state.aiGeneratedMessageSlice.value,
  );
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [areMessagesRefreshing, setAreMessagesRefreshing] =
    useState<boolean>(false);
  const [hoveredCopy, setHoveredCopy] = useState<string | null>(null);
  const [hoveredEdit, setHoveredEdit] = useState<string | null>(null);
  const messagesEndRef: React.MutableRefObject<HTMLDivElement | null> =
    useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const userDisplayName =
    currentSelectedConversation?.userId === currentUser?.id
      ? currentUser?.displayName
      : currentSelectedConversation?.displayName;

  const styles = useStyles();
  const dispatch = useDispatch();

  const isConversationSwitchRef = useRef<boolean>(false);

  useEffect((): void => {
    if (
      currentSelectedConversation?.createdAt !==
        currentSelectedConversation?.updatedAt &&
      !isMessageLoading
    ) {
      setAutoScrollEnabled(true);
    }
    isConversationSwitchRef.current = true;
  }, [currentSelectedConversation?.id]);

  useEffect((): void => {
    scrollToBottom();
  }, [currentMessages]);

  const scrollToBottom = (): void => {
    if (autoScrollEnabled && currentMessages.length > 0) {
      const behavior = isConversationSwitchRef.current ? "auto" : "smooth";
      isConversationSwitchRef.current = false;
      messagesEndRef.current?.scrollIntoView({ behavior });
    }
  };

  const copyToClipboard = (message: IMessage) => (): void => {
    // Find the rendered message content in the DOM
    const messageElement = document.querySelector(
      `[data-message-id="${message.id}"]`,
    );

    if (messageElement) {
      // Clone the element to modify without affecting the display
      const clonedElement = messageElement.cloneNode(true) as HTMLElement;

      // Remove all background colors from the cloned element and its children
      const removeBackgrounds = (element: HTMLElement) => {
        element.style.backgroundColor = "transparent";
        element.style.background = "transparent";

        // Remove background from all child elements
        const allElements = element.getElementsByTagName("*");
        for (let i = 0; i < allElements.length; i++) {
          const el = allElements[i] as HTMLElement;
          el.style.backgroundColor = "transparent";
          el.style.background = "transparent";
        }
      };

      removeBackgrounds(clonedElement);

      // Create a temporary container and append the cloned element
      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.left = "-9999px";
      document.body.appendChild(tempContainer);
      tempContainer.appendChild(clonedElement);

      // Select and copy the modified content
      const selection = window.getSelection();
      const range = document.createRange();

      selection?.removeAllRanges();
      range.selectNodeContents(tempContainer);
      selection?.addRange(range);

      // Copy the selection
      try {
        document.execCommand("copy");
        setCopiedMessageId(message.id ?? null);
      } catch (err) {
        console.error("Failed to copy: ", err);
        // Fallback to plain text
        navigator.clipboard.writeText(message.content);
        setCopiedMessageId(message.id ?? null);
      }

      // Clean up
      selection?.removeAllRanges();
      document.body.removeChild(tempContainer);
    } else {
      // Fallback if element not found
      navigator.clipboard.writeText(message.content);
      setCopiedMessageId(message.id ?? null);
    }
  };

  const setEditMessage = (message: IMessage) => (): void => {
    setIsEditMessageActive(true);
    setPromptToEdit(message.content);
  };

  const containsAIMessage: boolean =
    currentMessages?.filter((message: IMessage) => message.role === "assistant")
      ?.length > 0;

  const handleRefresh = async (): Promise<void> => {
    setAreMessagesRefreshing(true);
    try {
      const data = await getMessagesOfConversation({
        conversationId: currentSelectedConversation.id,
      });
      dispatch(setCurrentMessages(data));
    } finally {
      setAreMessagesRefreshing(false);
    }
  };

  const canInteractWithConversation = (): boolean => {
    if (activeWorkspace?.archived) {
      return false;
    }
    return true;
  };

  const handleAddComment = async (
    messageId: string | undefined,
    content: string,
  ): Promise<void> => {
    if (!messageId || !content?.trim()) return;

    try {
      const updatedConversation = await addCommentToMessage({
        messageId,
        content,
      });

      dispatch(setCurrentMessages(updatedConversation.messages));
      await handleRefresh();
    } catch (error) {
      console.error("Failed to add comment:", error);
    }
  };

  return (
    <div className={styles.container}>
      <div
        ref={messagesContainerRef}
        className={mergeClasses(
          styles.messageWrapper,
          isMobile && styles.mobileFullWidth,
        )}
      >
        <div className={styles.stickyHeader}>
          {areMessagesRefreshing ? (
            <div className={styles.refreshingSpinner}>
              <Spinner label="Refreshing messages..." />
            </div>
          ) : (
            <></>
          )}
        </div>
        <div className={styles.messageContainer}>
          {!currentSelectedConversation?.id ? (
            <div
              className={mergeClasses(
                styles.noMessagesContainer,
                isLoadingTemplates
                  ? styles.noMessagesContainerHidden
                  : styles.noMessagesContainerVisible,
              )}
            >
              {!isMobile && (
                <>
                  <Text className={styles.noMessagesText}>
                    Ask me anything about your documents, code, or
                    <br />
                    projects. I'm here to help!
                  </Text>

                  {chatTemplates.length > 0 && (
                    <div className={styles.templateButtonsContainer}>
                      {chatTemplates.map((template) => (
                        <Tooltip
                          key={template.id}
                          content={template.description || template.overview}
                          relationship="description"
                        >
                          <Button
                            appearance="outline"
                            size="medium"
                            onClick={() => onUseTemplate(template)}
                            className={styles.templateButton}
                          >
                            <span className={styles.templateButtonIcon}>
                              {renderTemplateIcon(template.icon)}
                            </span>
                            <span className={styles.templateButtonText}>
                              {template.name}
                            </span>
                          </Button>
                        </Tooltip>
                      ))}
                      <Tooltip
                        content="Explore Quick Start Gallery"
                        relationship="label"
                      >
                        <Button
                          appearance="outline"
                          size="medium"
                          icon={<Apps24Regular />}
                          onClick={onOpenGallery}
                          className={styles.galleryIconButton}
                        />
                      </Tooltip>
                    </div>
                  )}

                  {chatTemplates.length === 0 && (
                    <Tooltip
                      content="Explore Quick Start Gallery"
                      relationship="label"
                    >
                      <Button
                        appearance="outline"
                        size="medium"
                        icon={<Apps24Regular />}
                        onClick={onOpenGallery}
                        className={styles.galleryIconButton}
                      />
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          ) : (
            <></>
          )}
          {currentSelectedConversation?.id && !currentMessages?.length ? (
            [1, 2, 3]?.map(
              (i: number): JSX.Element => (
                <SkeletonChat key={i} avatarOnRight={i % 2 === 1} />
              ),
            )
          ) : (
            <></>
          )}
          <div
            className={
              areMessagesRefreshing ? styles.refreshingOverlay : undefined
            }
          >
            {currentMessages.map((message: IMessage, index: number) => (
              <div
                key={index}
                className={`${styles.messageDetails} ${message?.role === "assistant" ? styles.assistantMessage : styles.userMessage}`}
              >
                <Persona
                  name={
                    message?.role === "assistant"
                      ? "NorthHighland AI"
                      : (message.displayName ?? userDisplayName)
                  }
                  secondaryText={message?.role}
                  size="large"
                  avatar={
                    message?.role === "assistant"
                      ? { image: { src: "/images/NH_Logo_Circle.png" } }
                      : { name: message.displayName ?? userDisplayName }
                  }
                />
                {message.chartData ? (
                  <div className={styles.chartWrapper}>
                    <Charts chartData={message.chartData} />
                  </div>
                ) : (
                  <Body2
                    className={mergeClasses(
                      styles.messageContent,
                      isMobile && styles.messageContentMobile,
                    )}
                    data-message-id={message.id}
                  >
                    {renderContentWithCitations(
                      message.content,
                      message.citations,
                    )}
                    {message.comments?.map((c: IMessageComment) => (
                      <MessageComment key={c.id} comment={c} />
                    ))}
                  </Body2>
                )}
                <div className={styles.tagWrapper}>
                  <div className={styles.tagActionsRow}>
                    {message?.role !== "assistant" &&
                      !activeWorkspace?.archived && (
                        <div className={styles.actionButtonsGroup}>
                          {copiedMessageId === message?.id ? (
                            <Button
                              size="small"
                              appearance="subtle"
                              icon={<Clipboard16Filled />}
                              onClick={copyToClipboard(message)}
                              aria-label="Copied"
                            />
                          ) : (
                            <Button
                              size="small"
                              appearance="subtle"
                              icon={
                                hoveredCopy === message.id ? (
                                  <Clipboard16Filled />
                                ) : (
                                  <Clipboard16Regular />
                                )
                              }
                              onClick={copyToClipboard(message)}
                              aria-label="Copy"
                              onMouseEnter={() =>
                                setHoveredCopy(message.id ?? null)
                              }
                              onMouseLeave={() => setHoveredCopy(null)}
                            />
                          )}
                          {!isEditMessageActive && (
                            <Button
                              onClick={setEditMessage(message)}
                              size="small"
                              appearance="subtle"
                              icon={
                                hoveredEdit === message.id ? (
                                  <Edit16Filled />
                                ) : (
                                  <Edit16Regular />
                                )
                              }
                              aria-label="Edit"
                              onMouseEnter={() =>
                                setHoveredEdit(message.id ?? null)
                              }
                              onMouseLeave={() => setHoveredEdit(null)}
                            />
                          )}
                        </div>
                      )}
                    {message?.llmConfig?.sourceRagCategories?.length > 0 && (
                      <Tag size="small" appearance="outline">
                        {message?.llmConfig?.sourceRagCategories.join(", ")}
                      </Tag>
                    )}
                    {message?.role === "assistant" && (
                      <Tag size="small">{message?.llmConfig?.llmType}</Tag>
                    )}
                  </div>
                  {message?.role === "assistant" &&
                    canInteractWithConversation() && (
                      <div className={styles.actionButtonsGroup}>
                        <ThumbsFeedback
                          message={message}
                          previousMessage={currentMessages[index - 1]}
                        />
                        {!activeWorkspace?.archived && (
                          <>
                            {copiedMessageId === message?.id ? (
                              <Button
                                size="small"
                                appearance="subtle"
                                icon={<Clipboard16Filled />}
                                onClick={copyToClipboard(message)}
                                aria-label="Copied"
                              />
                            ) : (
                              <Button
                                size="small"
                                appearance="subtle"
                                icon={
                                  hoveredCopy === message.id ? (
                                    <Clipboard16Filled />
                                  ) : (
                                    <Clipboard16Regular />
                                  )
                                }
                                onClick={copyToClipboard(message)}
                                aria-label="Copy"
                                onMouseEnter={() =>
                                  setHoveredCopy(message.id ?? null)
                                }
                                onMouseLeave={() => setHoveredCopy(null)}
                              />
                            )}
                            <AddCommentDialog
                              messageId={
                                message.id ?? ConstantValues.EMPTY_STRING
                              }
                              onSubmit={handleAddComment}
                            />
                          </>
                        )}
                      </div>
                    )}
                </div>
                <div className={styles.fileCard}>
                  {message?.files?.map((file: IFile, index) => (
                    <Card key={index} className={styles.fileCard} size="small">
                      <CardHeader
                        className={styles.fileCardHeader}
                        image={{
                          as: "img",
                          src: resolveAsset(file?.filename),
                          alt: "Word app logo",
                          className: styles.cardHeaderImage,
                        }}
                        header={
                          <Link
                            onClick={downloadFile(
                              file.url ?? ConstantValues.EMPTY_STRING,
                            )}
                            title={decodeURI(
                              file?.filename?.split("/")?.at(-1) ||
                                ConstantValues.EMPTY_STRING,
                            )}
                            className={styles.fileLinkTruncated}
                          >
                            {decodeURI(
                              file?.filename?.split("/")?.at(-1) ||
                                ConstantValues.EMPTY_STRING,
                            )}
                          </Link>
                        }
                      />
                    </Card>
                  ))}
                </div>
                {message.webSearchCitations &&
                  message.webSearchCitations.length > 0 && (
                    <div className={styles.sectionWrapper}>
                      <div className={styles.sectionTitle}>
                        Web Search Citations
                      </div>

                      {message.webSearchCitations.length > 3 ? (
                        <>
                          {message.webSearchCitations
                            .slice(0, 3)
                            .map((citation, index) => (
                              <div
                                key={index}
                                className={styles.webCitationCard}
                              >
                                <a
                                  href={citation.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <strong>{citation.title}</strong>
                                  <p>
                                    {citation.cited_text ||
                                      "Source available at link."}
                                  </p>
                                </a>
                              </div>
                            ))}
                          <Accordion collapsible>
                            <AccordionItem value="moreWebCitations">
                              <AccordionHeader>
                                +{message.webSearchCitations.length - 3} more
                                sources
                              </AccordionHeader>
                              <AccordionPanel>
                                {message.webSearchCitations
                                  .slice(3)
                                  .map((citation, index) => (
                                    <div
                                      key={index}
                                      className={styles.webCitationCard}
                                    >
                                      <a
                                        href={citation.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <strong>{citation.title}</strong>
                                        <p>
                                          {citation.cited_text ||
                                            "Source available at link."}
                                        </p>
                                      </a>
                                    </div>
                                  ))}
                              </AccordionPanel>
                            </AccordionItem>
                          </Accordion>
                        </>
                      ) : (
                        message.webSearchCitations.map((citation, index) => (
                          <div key={index} className={styles.webCitationCard}>
                            <a
                              href={citation.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <strong>{citation.title}</strong>
                              <p>
                                {citation.cited_text ||
                                  "Source available at link."}
                              </p>
                            </a>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                {!message?.sourceDocuments ? (
                  <></>
                ) : message.sourceDocuments.length > 2 ? (
                  <div className={styles.sectionWrapper}>
                    <div className={styles.sectionTitle}>
                      North Highland Sources
                    </div>
                    {message.sourceDocuments
                      .slice(0, 2)
                      .map((document: any, index: number) => {
                        const { citationKeyMap } = buildCitationKeyMap(
                          message.citations ?? [],
                        );

                        const matchingCitations =
                          message.citations?.filter(
                            (c: ICitation) => c.file_name === document.filename,
                          ) ?? [];

                        let citationId: number | null = null;
                        for (const citation of matchingCitations) {
                          const key = getCitationKey(citation);
                          const id = citationKeyMap.get(key);
                          if (
                            id !== undefined &&
                            (citationId === null || id < citationId)
                          ) {
                            citationId = id;
                          }
                        }

                        return (
                          <Card key={index} className={styles.sourceDocCard}>
                            <CardHeader
                              image={{
                                as: "img",
                                src: resolveAsset(document?.filename, document),
                                alt: "Document icon",
                                className: styles.cardHeaderImage,
                              }}
                              header={
                                <div>
                                  <Link
                                    onClick={downloadFile(
                                      document.filename ??
                                        ConstantValues.EMPTY_STRING,
                                    )}
                                    title={document?.filename}
                                    className={styles.cursorPointer}
                                  >
                                    {document?.filename}
                                  </Link>
                                  {citationId && (
                                    <Text className={styles.citationIdText}>
                                      [Citation ID: {citationId}]
                                    </Text>
                                  )}
                                </div>
                              }
                            />
                            <p className={styles.sourceDocContent}>
                              {document?.content}
                            </p>
                          </Card>
                        );
                      })}
                    <Accordion collapsible>
                      <AccordionItem value="additionalSourceDocs">
                        <AccordionHeader>
                          +{message.sourceDocuments.length - 2} more
                        </AccordionHeader>
                        <AccordionPanel>
                          {message.sourceDocuments
                            .slice(2)
                            .map((document: any, index: number) => {
                              const { citationKeyMap } = buildCitationKeyMap(
                                message.citations ?? [],
                              );

                              const matchingCitations =
                                message.citations?.filter(
                                  (c: ICitation) =>
                                    c.file_name === document.filename,
                                ) ?? [];

                              let citationId: number | null = null;
                              for (const citation of matchingCitations) {
                                const key = getCitationKey(citation);
                                const id = citationKeyMap.get(key);
                                if (
                                  id !== undefined &&
                                  (citationId === null || id < citationId)
                                ) {
                                  citationId = id;
                                }
                              }

                              return (
                                <Card
                                  key={index}
                                  className={styles.sourceDocCard}
                                >
                                  <CardHeader
                                    image={{
                                      as: "img",
                                      src: resolveAsset(
                                        document?.filename,
                                        document,
                                      ),
                                      alt: "Document icon",
                                      className: styles.cardHeaderImage,
                                    }}
                                    header={
                                      <div>
                                        <Link
                                          onClick={downloadFile(
                                            document.filename ??
                                              ConstantValues.EMPTY_STRING,
                                          )}
                                          title={document?.filename}
                                          className={styles.cursorPointer}
                                        >
                                          {document?.filename}
                                        </Link>
                                        {citationId && (
                                          <Text
                                            className={styles.citationIdText}
                                          >
                                            [Citation ID: {citationId}]
                                          </Text>
                                        )}
                                      </div>
                                    }
                                  />
                                  <p className={styles.sourceDocContent}>
                                    {document?.content}
                                  </p>
                                </Card>
                              );
                            })}
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </div>
                ) : (
                  message?.sourceDocuments?.map(
                    (document: any, index: number) => (
                      <Card key={index} className={styles.sourceDocCard}>
                        <CardHeader
                          image={{
                            as: "img",
                            src: resolveAsset(document?.filename, document),
                            alt: "Document icon",
                            className: styles.cardHeaderImage,
                          }}
                          header={
                            <Link
                              onClick={downloadFile(
                                document.filename ??
                                  ConstantValues.EMPTY_STRING,
                              )}
                              title={document?.filename}
                              className={styles.fileLinkTruncated}
                            >
                              {document?.filename}
                            </Link>
                          }
                        />
                        <p className={styles.sourceDocContent}>
                          {document?.content}
                        </p>
                      </Card>
                    ),
                  )
                )}
                {message.referencedWorkspaceConversations &&
                  message.referencedWorkspaceConversations.length > 0 &&
                  message?.role === "assistant" && (
                    <div className={styles.referenceSectionMargin}>
                      <div className={styles.referenceSectionHeader}>
                        <Pin16Regular />
                        <Text size={200} weight="semibold">
                          Referenced Chats:
                        </Text>
                      </div>
                      <div className={styles.referenceSectionGrid}>
                        {message.referencedWorkspaceConversations.map(
                          (referencedMessage) => (
                            <Tag
                              key={referencedMessage.id}
                              size="small"
                              appearance="brand"
                              shape="rounded"
                              title={referencedMessage.title}
                              className={styles.referenceTag}
                            >
                              {referencedMessage.title}
                            </Tag>
                          ),
                        )}
                      </div>
                    </div>
                  )}
                {message.tags &&
                  message.tags.length > 0 &&
                  message?.role === "assistant" && (
                    <div className={styles.referenceSectionMargin}>
                      <div className={styles.referenceSectionHeader}>
                        <Settings16Regular />
                        <Text size={200} weight="semibold">
                          Knowledge Components:
                        </Text>
                      </div>
                      <div className={styles.referenceSectionGrid}>
                        {message.tags.map((tag) => (
                          <Tag
                            key={tag.id}
                            size="small"
                            appearance="brand"
                            shape="rounded"
                            title={tag.name}
                            className={styles.referenceTag}
                          >
                            {tag.name}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}
                {message.selectedLibraryDocuments &&
                  message.selectedLibraryDocuments.length > 0 &&
                  message?.role === "assistant" && (
                    <div className={styles.referenceSectionMargin}>
                      <div className={styles.referenceSectionHeader}>
                        <Document16Regular />
                        <Text size={200} weight="semibold">
                          Workspace Library Documents:
                        </Text>
                      </div>
                      <div className={styles.referenceSectionGrid}>
                        {message.selectedLibraryDocuments.map((doc) => (
                          <Tag
                            key={doc.filename}
                            size="small"
                            appearance="brand"
                            shape="rounded"
                            title={doc.originalFilename}
                            className={styles.referenceTag}
                          >
                            {doc.originalFilename}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}
                <Text italic size={100} className={styles.dateStyle}>
                  {getTimeAndDate(message.createdAt)}
                </Text>
              </div>
            ))}
            <AIResponse
              isLoading={isMessageLoading}
              aiMessage={aiGeneratedMessage}
            />
            <div ref={messagesEndRef} />
            {containsAIMessage && (
              <Caption2 className={styles.captionWrapper}>
                This is an AI, results may be innacurate. Please double-check
                responses and use our{" "}
                <Link
                  onClick={() => {
                    navigate("/guidelines");
                  }}
                >
                  {" "}
                  <Caption2 className={styles.guidelinesLink}>
                    guidelines
                  </Caption2>
                </Link>
              </Caption2>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
